import {useEffect, useMemo, useState} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableSortLabel,
    CircularProgress,
    Box,
} from "@mui/material";
import {apiGet} from "../utils";

const formatMoney = (v) => (v == null ? "-" : `$${Math.round(v).toLocaleString()}`);
const formatQty = (v) => (v == null ? "-" : Math.round(v).toLocaleString());
const formatPct = (v) => (v == null ? "-" : `${v > 0 ? "+" : ""}${v}%`);

const COLUMNS = [
    {key: "product_name", label: "Product", numeric: false},
    {key: "product_sku", label: "SKU #", numeric: false},
    {key: "recent_revenue", label: "Current Revenue", numeric: true},
    {key: "recent_qty", label: "Current Qty", numeric: true},
    {key: "prior_revenue", label: "Prior Revenue", numeric: true},
    {key: "prior_qty", label: "Prior Qty", numeric: true},
];
const REDUCED_COLUMN = {key: "revenue_change_pct", label: "Change", numeric: true};

const sortSkus = (skus, orderBy, order) => {
    if (!orderBy) {
        return skus;
    }
    const direction = order === "desc" ? -1 : 1;
    return [...skus].sort((a, b) => {
        const aVal = a[orderBy];
        const bVal = b[orderBy];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (typeof aVal === "string") {
            return aVal.localeCompare(bVal) * direction;
        }
        return (aVal - bVal) * direction;
    });
};

export const SkuDetailModal = ({open, accountId, type, onClose}) => {
    const [loading, setLoading] = useState(false);
    const [skus, setSkus] = useState([]);
    const [orderBy, setOrderBy] = useState(null);
    const [order, setOrder] = useState("asc");

    useEffect(() => {
        if (!open || accountId == null || !type) {
            return;
        }
        setLoading(true);
        setSkus([]);
        setOrderBy(null);
        setOrder("asc");
        apiGet(`v2/agent/sales-performance/skus?account_id=${accountId}&type=${type}`, (resp) => {
            setSkus(resp?.success ? (resp.data?.skus || []) : []);
            setLoading(false);
        });
    }, [open, accountId, type]);

    const columns = type === "reduced" ? [...COLUMNS, REDUCED_COLUMN] : COLUMNS;
    const sortedSkus = useMemo(() => sortSkus(skus, orderBy, order), [skus, orderBy, order]);

    const handleSort = (key) => {
        if (orderBy === key) {
            setOrder(order === "asc" ? "desc" : "asc");
        } else {
            setOrderBy(key);
            setOrder("asc");
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{type === "stopped" ? "SKUs Stopped" : "SKUs Reduced"}</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{display: "flex", justifyContent: "center", p: 3}}>
                        <CircularProgress size={24}/>
                    </Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                {columns.map((col) => (
                                    <TableCell key={col.key} align={col.numeric ? "right" : "left"}>
                                        <TableSortLabel
                                            active={orderBy === col.key}
                                            direction={orderBy === col.key ? order : "asc"}
                                            onClick={() => handleSort(col.key)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedSkus.map((s, i) => (
                                <TableRow key={s.product_id}>
                                    <TableCell>{i + 1}</TableCell>
                                    <TableCell>{s.product_name}</TableCell>
                                    <TableCell>{s.product_sku}</TableCell>
                                    <TableCell align="right">{formatMoney(s.recent_revenue)}</TableCell>
                                    <TableCell align="right">{formatQty(s.recent_qty)}</TableCell>
                                    <TableCell align="right">{formatMoney(s.prior_revenue)}</TableCell>
                                    <TableCell align="right">{formatQty(s.prior_qty)}</TableCell>
                                    {type === "reduced" && (
                                        <TableCell align="right">{formatPct(s.revenue_change_pct)}</TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {!sortedSkus.length && (
                                <TableRow>
                                    <TableCell colSpan={columns.length + 1}>No SKUs found.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
